
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, User, Loader2, Key } from 'lucide-react';
import { useSidebar } from '@/components/ui/sidebar';
import { ChangePasswordDialog } from '@/components/admin/ChangePasswordDialog';

export function UserProfileSection() {
  const { signOut, profile } = useAuth();
  const { state } = useSidebar();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const isCollapsed = state === "collapsed";

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    
    setIsLoggingOut(true);
    
    try {
      await signOut();
    } catch (error) {
      // Even if logout fails, the signOut function will handle redirection
    } finally {
      // This might not execute if we redirect, but it's good practice
      setIsLoggingOut(false);
    }
  };

  const handleProfileClick = () => {
    navigate('/admin/profile');
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  if (isCollapsed) {
    return (
      <>
        <div className="p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full h-auto p-2 hover:bg-accent/80 transition-all duration-200" 
                disabled={isLoggingOut}
              >
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="text-sm font-medium bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
                    {getInitials(profile?.full_name, profile?.email || '')}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" className="w-72 bg-background/95 backdrop-blur-sm border shadow-xl">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1 py-2 text-left">
                  <p className="text-sm font-semibold leading-none truncate text-left">
                    {profile?.full_name || profile?.email}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize font-medium text-left">
                    {profile?.role}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleProfileClick}
                disabled={isLoggingOut} 
                className="cursor-pointer py-2.5"
              >
                <User className="mr-3 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setChangePasswordOpen(true)} 
                disabled={isLoggingOut}
                className="cursor-pointer py-2.5"
              >
                <Key className="mr-3 h-4 w-4" />
                <span>Change Password</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout} 
                disabled={isLoggingOut}
                className="cursor-pointer text-destructive focus:text-destructive py-2.5"
              >
                {isLoggingOut ? (
                  <Loader2 className="mr-3 h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="mr-3 h-4 w-4" />
                )}
                <span>{isLoggingOut ? 'Signing out...' : 'Log out'}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <ChangePasswordDialog
          open={changePasswordOpen}
          onOpenChange={setChangePasswordOpen}
        />
      </>
    );
  }

  return (
    <>
      <div className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-start h-auto p-2 hover:bg-accent/80 transition-all duration-200 rounded-lg" 
              disabled={isLoggingOut}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="text-sm font-medium bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
                    {getInitials(profile?.full_name, profile?.email || '')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start min-w-0 flex-1 space-y-0.5 text-left">
                  <div className="text-sm font-semibold truncate w-full leading-tight text-left">
                    {profile?.full_name || profile?.email}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize font-medium text-left">
                    {profile?.role}
                  </div>
                </div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" className="w-72 bg-background/95 backdrop-blur-sm border shadow-xl">
            <DropdownMenuLabel className="py-3 text-left">
              <div className="font-semibold text-left">My Account</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleProfileClick}
              disabled={isLoggingOut} 
              className="cursor-pointer py-3"
            >
              <User className="mr-3 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setChangePasswordOpen(true)} 
              disabled={isLoggingOut}
              className="cursor-pointer py-3"
            >
              <Key className="mr-3 h-4 w-4" />
              <span>Change Password</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout} 
              disabled={isLoggingOut}
              className="cursor-pointer text-destructive focus:text-destructive py-3"
            >
              {isLoggingOut ? (
                <Loader2 className="mr-3 h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="mr-3 h-4 w-4" />
              )}
              <span>{isLoggingOut ? 'Signing out...' : 'Log out'}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </>
  );
}
